/* ===================================
   GRYFFIN CUSTOM JAVASCRIPT
   Custom animations, interactions, and effects
   
   Installation:
   1. Save this as: assets/gryffin-custom.js
   2. Add to layout/theme.liquid before </body>:
      <script src="{{ 'gryffin-custom.js' | asset_url }}" defer></script>
   =================================== */

(function() {
  'use strict';

  // ===================================
  // SCROLL ANIMATIONS
  // ===================================
  
  /**
   * Animate elements on scroll using Intersection Observer
   * Supports multiple animation types via data attributes
   */
  const initScrollAnimations = () => {
    const elements = document.querySelectorAll('.scroll-animate, [data-scroll-animation]');
    
    if (elements.length === 0) return;
    
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;
          const delay = element.dataset.animationDelay || '0';
          
          // Apply animation after delay
          setTimeout(() => {
            element.classList.add('is-visible');
            
            // Add specific animation class if specified
            const animationType = element.dataset.scrollAnimation;
            if (animationType && animationType !== 'none') {
              element.classList.add(`animate-${animationType}`);
            }
          }, parseInt(delay));
          
          // Optional: Unobserve after animation
          if (element.dataset.animateOnce !== 'false') {
            observer.unobserve(element);
          }
        } else if (element.dataset.animateOnce === 'false') {
          element.classList.remove('is-visible');
          
          // Remove animation class for re-trigger
          const animationType = element.dataset.scrollAnimation;
          if (animationType && animationType !== 'none') {
            element.classList.remove(`animate-${animationType}`);
          }
        }
      });
    }, observerOptions);
    
    elements.forEach(element => observer.observe(element));
  };
  
  // ===================================
  // PARALLAX SCROLLING
  // ===================================
  
  /**
   * Apply parallax effect to elements with data-parallax-speed
   * Usage: <div data-parallax-speed="0.5">Content</div>
   */
  const initParallax = () => {
    const parallaxElements = document.querySelectorAll('[data-parallax-speed]');
    
    if (parallaxElements.length === 0) return;
    
    let ticking = false;
    
    const updateParallax = () => {
      const scrolled = window.pageYOffset;
      
      parallaxElements.forEach(element => {
        const speed = parseFloat(element.dataset.parallaxSpeed) || 0.5;
        const rect = element.getBoundingClientRect();
        const elementTop = rect.top + scrolled;
        const elementHeight = rect.height;
        const windowHeight = window.innerHeight;
        
        // Only apply parallax when element is in viewport
        if (scrolled + windowHeight > elementTop && scrolled < elementTop + elementHeight) {
          const yPos = -(scrolled - elementTop) * speed;
          element.style.transform = `translateY(${yPos}px)`;
        }
      });
      
      ticking = false;
    };
    
    const requestTick = () => {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', requestTick, { passive: true });
  };
  
  // ===================================
  // SMOOTH SCROLL TO ANCHORS
  // ===================================
  
  /**
   * Smooth scroll to anchor links
   */
  const initSmoothScroll = () => {
    const anchorLinks = document.querySelectorAll('a[href^="#"]');
    
    anchorLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        
        if (href === '#') return;
        
        const target = document.querySelector(href);
        
        if (target) {
          e.preventDefault();
          
          const offsetTop = target.offsetTop - 100; // Adjust offset as needed
          
          window.scrollTo({
            top: offsetTop,
            behavior: 'smooth'
          });
          
          // Update URL without jumping
          if (history.pushState) {
            history.pushState(null, null, href);
          }
        }
      });
    });
  };
  
  // ===================================
  // CUSTOM SLIDER CLASS
  // ===================================
  
  /**
   * Custom slider with touch support
   * Usage: new GryffinSlider(element, options)
   */
  class GryffinSlider {
    constructor(element, options = {}) {
      this.slider = element;
      this.slides = element.querySelectorAll('[data-slide]');
      this.currentIndex = 0;
      this.autoplay = options.autoplay ?? true;
      this.interval = options.interval ?? 5000;
      this.loop = options.loop ?? true;
      this.timer = null;
      this.startX = 0;
      this.isDragging = false;
      
      if (this.slides.length === 0) return;
      
      this.init();
    }
    
    init() {
      this.addTouchSupport();
      this.addKeyboardSupport();
      
      if (this.autoplay) {
        this.startAutoplay();
        this.pauseOnHover();
      }
    }
    
    goToSlide(index) {
      if (index < 0 || index >= this.slides.length) return;
      
      this.slides[this.currentIndex].classList.remove('active');
      this.currentIndex = index;
      this.slides[this.currentIndex].classList.add('active');
      
      // Trigger custom event
      this.slider.dispatchEvent(new CustomEvent('slideChange', {
        detail: { index: this.currentIndex }
      }));
      
      if (this.autoplay) {
        this.resetAutoplay();
      }
    }
    
    nextSlide() {
      let next = this.currentIndex + 1;
      
      if (next >= this.slides.length) {
        next = this.loop ? 0 : this.currentIndex;
      }
      
      this.goToSlide(next);
    }
    
    prevSlide() {
      let prev = this.currentIndex - 1;
      
      if (prev < 0) {
        prev = this.loop ? this.slides.length - 1 : 0;
      }
      
      this.goToSlide(prev);
    }
    
    startAutoplay() {
      if (this.slides.length <= 1) return;
      this.timer = setInterval(() => this.nextSlide(), this.interval);
    }
    
    stopAutoplay() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }
    
    resetAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
    
    pauseOnHover() {
      this.slider.addEventListener('mouseenter', () => this.stopAutoplay());
      this.slider.addEventListener('mouseleave', () => {
        if (this.autoplay) this.startAutoplay();
      });
    }
    
    addTouchSupport() {
      this.slider.addEventListener('touchstart', (e) => {
        this.startX = e.touches[0].clientX;
        this.isDragging = true;
      }, { passive: true });
      
      this.slider.addEventListener('touchmove', (e) => {
        if (!this.isDragging) return;
      }, { passive: true });
      
      this.slider.addEventListener('touchend', (e) => {
        if (!this.isDragging) return;
        
        const endX = e.changedTouches[0].clientX;
        const diff = this.startX - endX;
        
        if (Math.abs(diff) > 50) {
          if (diff > 0) {
            this.nextSlide();
          } else {
            this.prevSlide();
          }
        }
        
        this.isDragging = false;
      });
    }
    
    addKeyboardSupport() {
      document.addEventListener('keydown', (e) => {
        if (!this.slider.matches(':hover')) return;
        
        if (e.key === 'ArrowLeft') {
          this.prevSlide();
        } else if (e.key === 'ArrowRight') {
          this.nextSlide();
        }
      });
    }
  }
  
  // Make GryffinSlider available globally
  window.GryffinSlider = GryffinSlider;
  
  // ===================================
  // LAZY LOADING IMAGES
  // ===================================
  
  /**
   * Lazy load images with data-src attribute
   * Usage: <img data-src="image.jpg" class="lazy-load" alt="">
   */
  const initLazyLoad = () => {
    const lazyImages = document.querySelectorAll('img[data-src]');
    
    if (lazyImages.length === 0) return;
    
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src;
          img.classList.add('loaded');
          imageObserver.unobserve(img);
        }
      });
    });
    
    lazyImages.forEach(img => imageObserver.observe(img));
  };
  
  // ===================================
  // VIDEO BACKGROUND AUTO-PLAY
  // ===================================
  
  /**
   * Auto-play video backgrounds when in viewport
   */
  const initVideoBackgrounds = () => {
    const videos = document.querySelectorAll('video[data-autoplay-viewport]');
    
    if (videos.length === 0) return;
    
    const videoObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target;
        
        if (entry.isIntersecting) {
          video.play().catch(e => console.log('Video autoplay failed:', e));
        } else {
          video.pause();
        }
      });
    }, { threshold: 0.5 });
    
    videos.forEach(video => videoObserver.observe(video));
  };
  
  // ===================================
  // MODAL/POPUP FUNCTIONALITY
  // ===================================
  
  /**
   * Simple modal system
   * Usage: <button data-modal-trigger="modal-id">Open</button>
   *        <div data-modal="modal-id">Modal content</div>
   */
  const initModals = () => {
    const triggers = document.querySelectorAll('[data-modal-trigger]');
    const modals = document.querySelectorAll('[data-modal]');
    
    if (triggers.length === 0 || modals.length === 0) return;
    
    const openModal = (modalId) => {
      const modal = document.querySelector(`[data-modal="${modalId}"]`);
      if (modal) {
        modal.classList.add('is-open');
        document.body.classList.add('modal-open');
      }
    };
    
    const closeModal = (modal) => {
      modal.classList.remove('is-open');
      document.body.classList.remove('modal-open');
    };
    
    triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const modalId = trigger.dataset.modalTrigger;
        openModal(modalId);
      });
    });
    
    modals.forEach(modal => {
      // Close button
      const closeBtn = modal.querySelector('[data-modal-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => closeModal(modal));
      }
      
      // Close on overlay click
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          closeModal(modal);
        }
      });
    });
    
    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        modals.forEach(modal => {
          if (modal.classList.contains('is-open')) {
            closeModal(modal);
          }
        });
      }
    });
  };
  
  // ===================================
  // HEADER SCROLL BEHAVIOR
  // ===================================
  
  /**
   * Add class to header on scroll
   */
  const initHeaderScroll = () => {
    const header = document.querySelector('[data-header-scroll]');
    
    if (!header) return;
    
    let lastScroll = 0;
    let ticking = false;
    
    const updateHeader = () => {
      const currentScroll = window.pageYOffset;
      
      if (currentScroll > 100) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }
      
      // Hide header on scroll down, show on scroll up
      if (currentScroll > lastScroll && currentScroll > 200) {
        header.classList.add('is-hidden');
      } else {
        header.classList.remove('is-hidden');
      }
      
      lastScroll = currentScroll;
      ticking = false;
    };
    
    const requestTick = () => {
      if (!ticking) {
        requestAnimationFrame(updateHeader);
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', requestTick, { passive: true });
  };
  
  // ===================================
  // INITIALIZE ALL
  // ===================================
  
  const init = () => {
    initScrollAnimations();
    initParallax();
    initSmoothScroll();
    initLazyLoad();
    initVideoBackgrounds();
    initModals();
    initHeaderScroll();
    
    // Initialize custom sliders
    document.querySelectorAll('[data-custom-slider]').forEach(slider => {
      new GryffinSlider(slider, {
        autoplay: slider.dataset.autoplay !== 'false',
        interval: parseInt(slider.dataset.interval) || 5000,
        loop: slider.dataset.loop !== 'false'
      });
    });
    
    // Trigger custom event when everything is initialized
    document.dispatchEvent(new CustomEvent('gryffin:initialized'));
  };
  
  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  // ===================================
  // UTILITY FUNCTIONS
  // ===================================
  
  /**
   * Debounce function for performance
   */
  window.debounce = function(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };
  
  /**
   * Throttle function for performance
   */
  window.throttle = function(func, limit) {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  };
  
})();

/* ===================================
   USAGE EXAMPLES
   =================================== */

/*
// Example 1: Scroll Animation
<div class="scroll-animate" data-animate-once="false">
  This will fade in when scrolled into view
</div>

// Example 2: Parallax Effect
<div data-parallax-speed="0.5">
  This will move slower than the scroll
</div>

// Example 3: Custom Slider
<div data-custom-slider data-autoplay="true" data-interval="5000">
  <div data-slide>Slide 1</div>
  <div data-slide>Slide 2</div>
</div>

// Example 4: Modal
<button data-modal-trigger="example-modal">Open Modal</button>
<div data-modal="example-modal">
  <button data-modal-close>Close</button>
  Modal content here
</div>

// Example 5: Header with scroll behavior
<header data-header-scroll>
  Header content
</header>

// Example 6: Listen for initialization
document.addEventListener('gryffin:initialized', () => {
  console.log('All Gryffin scripts initialized!');
});
*/
